import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import {
  acknowledgeSupplierRevision,
  fetchOrderApproval,
  fetchClientApproved,
  fetchPoDocuments,
  fetchCertificate,
  fetchSupplierOptions,
  linkOrderToSupplier,
  fetchOrderActivity,
  fetchOrderSupplier,
  reissueReviewToken,
  revokeReviewToken,
  createCgOrder,
  deleteCgOrder,
  deleteCgTemplate,
  fetchCgAccess,
  fetchCgItems,
  fetchCgOrderCosts,
  setOrderCosts,
  fetchCgOrders,
  fetchCgProductTypes,
  fetchCgStatuses,
  fetchCgTemplates,
  fetchShipTargetDays,
  replaceCgItems,
  saveCgTemplate,
  fetchActiveReviewToken,
  fetchGateTasks,
  overrideFinancialGate,
  updateCgOrder,
  updateShipTargetDays,
  fetchGateState,
  markInvoicePaid,
  markInvoiceSent,
  sendPoToSupplier,
  setGateStep,
  fetchOrderClientLogo,
  setOrderClientLogo,
  setOrderReferenceFile,
  clearOrderReferenceFile,
  fetchOrderReferenceFiles,
  setShopifyOrderNo,
} from './queries';
import type { CgItemInput, CgOrderInput, CgOrderUpdate, ReferenceKind } from './queries';
import type { CgOrderType } from './types';
import type { BillLineInput } from './orderProfit';
import { uploadRevisedProof } from './clientLinkService';
import type { Json } from '../../types/database.types';

export const cgKeys = {
  all: ['cg'] as const,
  access: () => [...cgKeys.all, 'access'] as const,
  statuses: (pipeline: string) => [...cgKeys.all, 'statuses', pipeline] as const,
  productTypes: () => [...cgKeys.all, 'productTypes'] as const,
  orders: () => [...cgKeys.all, 'orders'] as const,
  items: () => [...cgKeys.all, 'items'] as const,
  costs: () => [...cgKeys.all, 'costs'] as const,
  shipTarget: () => [...cgKeys.all, 'shipTarget'] as const,
  templates: () => [...cgKeys.all, 'templates'] as const,
};

function useActorId(): string {
  const { access } = useAuth();
  return access.status === 'approved' ? access.profile.id : '';
}

/** Whether the signed-in person is behind the CG gate (mirrors RLS). */
export function useCgAccess() {
  const { client, access } = useAuth();
  return useQuery({
    queryKey: cgKeys.access(),
    queryFn: () => fetchCgAccess(client),
    enabled: access.status === 'approved',
    staleTime: Infinity,
    retry: false,
  });
}

/**
 * One pipeline's stages, or both.
 *
 * The pipeline is part of the cache key, not just the request. Without it the
 * Orders tab under All would fill the cache with both ladders and the board
 * would then draw In-Stock columns beside Custom ones — a stale-cache bug that
 * only appears after visiting the tabs in one particular order, which is the
 * kind nobody reproduces.
 *
 * Defaults to 'custom' so every caller that has not been taught about the
 * second ladder keeps behaving exactly as it did.
 */
export function useCgStatuses(pipeline: CgOrderType | 'all' = 'custom') {
  const { client } = useAuth();
  return useQuery({
    queryKey: cgKeys.statuses(pipeline),
    queryFn: () => fetchCgStatuses(client, pipeline),
    staleTime: Infinity,
    retry: false,
  });
}

export function useCgProductTypes() {
  const { client } = useAuth();
  return useQuery({
    queryKey: cgKeys.productTypes(),
    queryFn: () => fetchCgProductTypes(client),
    staleTime: Infinity,
    retry: false,
  });
}

export function useCgOrders() {
  const { client } = useAuth();
  return useQuery({
    queryKey: cgKeys.orders(),
    queryFn: () => fetchCgOrders(client),
    retry: false,
  });
}

export function useCgItems() {
  const { client } = useAuth();
  return useQuery({
    queryKey: cgKeys.items(),
    queryFn: () => fetchCgItems(client),
    retry: false,
  });
}

/** Every supplier bill line (cg_order_costs). */
export function useCgOrderCosts() {
  const { client } = useAuth();
  return useQuery({
    queryKey: cgKeys.costs(),
    queryFn: () => fetchCgOrderCosts(client),
    retry: false,
  });
}

export function useSetOrderCosts() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, lines }: { orderId: string; lines: BillLineInput[] }) =>
      setOrderCosts(client, orderId, lines),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: cgKeys.costs() });
    },
  });
}

export function useShipTargetDays() {
  const { client } = useAuth();
  return useQuery({
    queryKey: cgKeys.shipTarget(),
    queryFn: () => fetchShipTargetDays(client),
    retry: false,
  });
}

export function useUpdateShipTarget() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (days: number) => updateShipTargetDays(client, days),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: cgKeys.shipTarget() });
    },
  });
}

export function useCreateCgOrder() {
  const { client } = useAuth();
  const actorId = useActorId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CgOrderInput) => createCgOrder(client, actorId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: cgKeys.orders() });
      void queryClient.invalidateQueries({ queryKey: cgKeys.items() });
    },
  });
}

export function useUpdateCgOrder() {
  const { client } = useAuth();
  const actorId = useActorId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { orderId: string; update: CgOrderUpdate; items?: CgItemInput[] }) =>
      updateCgOrder(client, actorId, args.orderId, args.update).then(async (order) => {
        if (args.items !== undefined) {
          await replaceCgItems(client, args.orderId, args.items);
        }
        return order;
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: cgKeys.orders() });
      void queryClient.invalidateQueries({ queryKey: cgKeys.items() });
    },
  });
}

export function useDeleteCgOrder() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { orderId: string; reason?: string }) =>
      deleteCgOrder(client, args.orderId, args.reason ?? ''),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: cgKeys.orders() });
      void queryClient.invalidateQueries({ queryKey: cgKeys.items() });
    },
  });
}

export function useCgTemplates() {
  const { client } = useAuth();
  return useQuery({
    queryKey: cgKeys.templates(),
    queryFn: () => fetchCgTemplates(client),
    retry: false,
  });
}

export function useSaveCgTemplate() {
  const { client } = useAuth();
  const actorId = useActorId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { name: string; config: Json }) =>
      saveCgTemplate(client, actorId, args.name, args.config),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: cgKeys.templates() });
    },
  });
}

export function useDeleteCgTemplate() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (templateId: string) => deleteCgTemplate(client, templateId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: cgKeys.templates() });
    },
  });
}

/**
 * The order's live review link, for the revision notice.
 *
 * Disabled until an order id exists, so opening the New Order dialog does not
 * ask for a token that cannot exist yet.
 */
export function useOrderReviewToken(orderId: string | null) {
  const { client, access } = useAuth();
  return useQuery({
    queryKey: [...cgKeys.all, 'review-token', orderId ?? ''],
    queryFn: () => fetchActiveReviewToken(client, orderId ?? ''),
    enabled: access.status === 'approved' && orderId !== null,
    staleTime: 60_000,
    retry: false,
  });
}

/** An order's financial gate tasks, for the banner and the override dialog. */
export function useGateTasks(orderId: string | null) {
  const { client, access } = useAuth();
  return useQuery({
    queryKey: [...cgKeys.all, 'gate-tasks', orderId ?? ''],
    queryFn: () => fetchGateTasks(client, orderId ?? ''),
    enabled: access.status === 'approved' && orderId !== null,
    retry: false,
  });
}

export function useOverrideFinancialGate() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { orderId: string; status: string; reason: string }) =>
      overrideFinancialGate(client, input),
    onSuccess: () => {
      // Both: the order moved AND cg_activity gained a row.
      void queryClient.invalidateQueries({ queryKey: cgKeys.all });
    },
  });
}

/**
 * The factory this order's PO will go to.
 *
 * Its own query rather than a column on cg_orders, because the link lives on
 * supplier_handoffs and an order has no field pointing back. Disabled until an
 * order id exists so the New Order dialog does not ask about a row that has
 * not been created.
 */
export function useOrderSupplier(orderId: string | null) {
  const { client, access } = useAuth();
  return useQuery({
    queryKey: [...cgKeys.all, 'order-supplier', orderId ?? ''],
    queryFn: () => fetchOrderSupplier(client, orderId ?? ''),
    enabled: access.status === 'approved' && orderId !== null,
    retry: false,
  });
}

/**
 * An order's recorded history.
 *
 * Reads cg_activity, which fourteen writers have filled since the tracker was
 * built and which no screen has ever selected from. No migration was needed:
 * cg_activity_read has permitted CG members all along.
 */
/** Whether the client has signed this order off — the gate the PO button hits. */
export function useClientApproved(orderId: string | null) {
  const { client, access } = useAuth();
  return useQuery({
    queryKey: [...cgKeys.all, 'client-approved', orderId ?? ''],
    queryFn: () => fetchClientApproved(client, orderId ?? ''),
    enabled: access.status === 'approved' && orderId !== null,
    retry: false,
  });
}

/** Every PO issued for a job. The order's paper trail. */
export function usePoDocuments(orderId: string | null) {
  const { client, access } = useAuth();
  return useQuery({
    queryKey: [...cgKeys.all, 'po-documents', orderId ?? ''],
    queryFn: () => fetchPoDocuments(client, orderId ?? ''),
    enabled: access.status === 'approved' && orderId !== null,
    retry: false,
  });
}

/** The certificate of approval. Stays on the job forever, delivered or not. */
export function useCertificate(orderId: string | null) {
  const { client, access } = useAuth();
  return useQuery({
    queryKey: [...cgKeys.all, 'certificate', orderId ?? ''],
    queryFn: () => fetchCertificate(client, orderId ?? ''),
    enabled: access.status === 'approved' && orderId !== null,
    retry: false,
  });
}

/**
 * Every stage this order has passed through, with the day counts.
 *
 * TAP's own function, not the supplier one. Same columns today; two functions
 * because a column added for one audience must not arrive at the other by
 * somebody editing a shared select.
 */
export function useOrderStageEvents(orderId: string | null) {
  const { client, access } = useAuth();
  return useQuery({
    queryKey: [...cgKeys.all, 'stage-events', orderId ?? ''],
    queryFn: async () => {
      const { data, error } = await client.rpc('cg_order_stage_events', {
        p_order: orderId ?? '',
      });
      if (error) {
        throw new Error(error.message);
      }
      return data ?? [];
    },
    enabled: access.status === 'approved' && orderId !== null,
    retry: false,
  });
}

export function useOrderActivity(orderId: string | null) {
  const { client, access } = useAuth();
  return useQuery({
    queryKey: [...cgKeys.all, 'order-activity', orderId ?? ''],
    queryFn: () => fetchOrderActivity(client, orderId ?? ''),
    enabled: access.status === 'approved' && orderId !== null,
    retry: false,
  });
}

/** Active suppliers an order can be pointed at. */
export function useSupplierOptions() {
  const { client, access } = useAuth();
  return useQuery({
    queryKey: [...cgKeys.all, 'supplier-options'],
    queryFn: () => fetchSupplierOptions(client),
    enabled: access.status === 'approved',
    staleTime: 300_000,
    retry: false,
  });
}

export function useLinkOrderToSupplier() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { orderId: string; organizationId: string }) =>
      linkOrderToSupplier(client, input.orderId, input.organizationId),
    onSuccess: () => {
      // cgKeys.all: the link, the order's history and the Send PO gate all move.
      void queryClient.invalidateQueries({ queryKey: cgKeys.all });
    },
  });
}

export function useReissueReviewToken() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { orderId: string; days: number }) =>
      reissueReviewToken(client, input.orderId, input.days),
    onSuccess: () => {
      // cgKeys.all: the token query and the order's activity trail both change.
      void queryClient.invalidateQueries({ queryKey: cgKeys.all });
    },
  });
}

export function useRevokeReviewToken() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { orderId: string; reason: string }) =>
      revokeReviewToken(client, input.orderId, input.reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: cgKeys.all });
    },
  });
}

export function useAcknowledgeSupplierRevision() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (orderId: string) => acknowledgeSupplierRevision(client, orderId),
    onSuccess: () => {
      // cgKeys.all, not cgKeys.orders(): the acknowledgement writes cg_activity
      // as well, and the drawer showing the order's history is open on screen
      // at the moment this runs.
      void queryClient.invalidateQueries({ queryKey: cgKeys.all });
    },
  });
}

// ── The financial gate workflow (Batch 11a) ────────────────────────────────

export function useGateState(orderId: string | null) {
  const { client, access } = useAuth();
  return useQuery({
    queryKey: [...cgKeys.all, 'gate-state', orderId ?? ''],
    queryFn: () => fetchGateState(client, orderId ?? ''),
    enabled: access.status === 'approved' && orderId !== null,
    retry: false,
  });
}

export function useSetGateStep() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { orderId: string; slug: string; done: boolean }) =>
      setGateStep(client, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: cgKeys.all });
    },
  });
}

export function useMarkInvoicePaid() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (orderId: string) => markInvoicePaid(client, orderId),
    onSuccess: () => {
      // Broad on purpose: this moves date_paid, which the order table, the
      // production PO and the KPI strip all read.
      void queryClient.invalidateQueries({ queryKey: cgKeys.all });
    },
  });
}

/**
 * Records the delivery date and moves the order to Delivered.
 *
 * One call for both facts. Two would leave a window where the order reads
 * Delivered with today's date on it — and today is exactly the wrong answer
 * when the confirmation arrives on Monday for a Friday delivery.
 */
export function useMarkDelivered() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { orderId: string; on: string }) => {
      const { error } = await client.rpc('cg_mark_delivered', {
        p_order: input.orderId,
        p_on: input.on,
      });
      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: cgKeys.all });
    },
  });
}

/**
 * Records the day the client invoice went out.
 *
 * Its own mutation rather than a field on the order update, because the
 * database refuses a future date and a date after the invoice was paid, and
 * those refusals should arrive as this action failing rather than as a whole
 * drawer save failing for a reason somewhere in it.
 */
export function useMarkInvoiceSent() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { orderId: string; on: string }) => markInvoiceSent(client, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: cgKeys.all });
    },
  });
}

export function useSendPoToSupplier() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (orderId: string) => sendPoToSupplier(client, orderId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: cgKeys.all });
    },
  });
}

/**
 * The client's approval state for one order, for the drawer panel.
 *
 * Keyed per order like the gate and review-token hooks beside it, so opening a
 * different order does not read a stale answer.
 */
export function useOrderApproval(orderId: string | null) {
  const { client, access } = useAuth();
  return useQuery({
    queryKey: [...cgKeys.all, 'order-approval', orderId ?? ''],
    queryFn: () => fetchOrderApproval(client, orderId ?? ''),
    enabled: access.status === 'approved' && orderId !== null,
    retry: false,
  });
}

export function useUploadRevisedProof() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { orderId: string; component: string; file: File }) =>
      uploadRevisedProof(client, input),
    onSuccess: () => {
      // The panel, and anything counting approvals, both move.
      void queryClient.invalidateQueries({ queryKey: cgKeys.all });
    },
  });
}

/** The client's logo on this order, or null when none has been uploaded. */
export function useOrderClientLogo(orderId: string | null) {
  const { client, access } = useAuth();
  return useQuery({
    queryKey: [...cgKeys.all, 'client-logo', orderId ?? ''],
    queryFn: () => fetchOrderClientLogo(client, orderId ?? ''),
    enabled: access.status === 'approved' && orderId !== null,
    retry: false,
  });
}

export function useSetOrderClientLogo() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { orderId: string; file: File }) => setOrderClientLogo(client, input),
    onSuccess: () => {
      // The logo, the order's history, and the Send PO preview all move.
      void queryClient.invalidateQueries({ queryKey: cgKeys.all });
    },
  });
}

/** Every reference file on an order — the logo and the two extra slots. */
export function useOrderReferenceFiles(orderId: string | null) {
  const { client, access } = useAuth();
  return useQuery({
    queryKey: [...cgKeys.all, 'reference-files', orderId ?? ''],
    queryFn: () => fetchOrderReferenceFiles(client, orderId ?? ''),
    enabled: access.status === 'approved' && orderId !== null,
    retry: false,
  });
}

export function useSetOrderReferenceFile() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { orderId: string; kind: ReferenceKind; label: string; file: File }) =>
      setOrderReferenceFile(client, input),
    onSuccess: () => {
      // The slot, the order's history and the Send PO preview all move.
      void queryClient.invalidateQueries({ queryKey: cgKeys.all });
    },
  });
}

export function useClearOrderReferenceFile() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { orderId: string; kind: ReferenceKind }) =>
      clearOrderReferenceFile(client, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: cgKeys.all });
    },
  });
}

/** Sets a CG order's Shopify order number. See setShopifyOrderNo. */
export function useSetShopifyOrderNo(orderId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { orderNo: string; reason: string }) =>
      setShopifyOrderNo(client, { orderId, ...input }),
    // The number is on the order, in its activity, and in every list that
    // names it, so the whole CG tree is refetched rather than one key.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: cgKeys.all }),
  });
}
